import { Tag } from "@blueprintjs/core";
import { getIn } from "formik";
import {
  FGCustomInput,
  FGTextInput,
  IFGFormGroupProps,
  useFGContext,
} from "nsitools-react";
import * as React from "react";
import { useLanguages } from "../../hooks";

export interface IFGTranslationsProps
  extends Omit<IFGFormGroupProps, "children" | "name"> {
  translationsField?: string;
}

export const FGTranslations: React.FunctionComponent<IFGTranslationsProps> = ({
  translationsField = "dataTranslation",
  label,
  ...fieldProps
}) => {
  const { languages, loadingLanguages } = useLanguages();
  const { formik, loading } = useFGContext();

  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    const currentDataTranslation = getIn(formik.values, `${translationsField}`);

    const currentTranslationsInDto =
      currentDataTranslation?.dataTranslationValues;
    if (
      initialized ||
      !currentTranslationsInDto ||
      languages?.length === 0 ||
      loadingLanguages ||
      loading
    )
      return;

    const missingLanguages = languages?.filter(
      (c) =>
        !currentTranslationsInDto.flatMap((cn) => cn.languageId).includes(c.id)
    );

    missingLanguages?.forEach((ml) => {
      currentTranslationsInDto.push({ languageId: ml.id });
    });

    formik.setFieldValue(translationsField, currentDataTranslation);
    setInitialized(true);
  }, [
    formik,
    formik.values,
    initialized,
    languages,
    loading,
    loadingLanguages,
    translationsField,
  ]);

  const getFieldName = React.useCallback(
    (languageId: number) => {
      if (!formik?.values) return "";
      if (!formik.values[translationsField]) {
        throw new Error(
          "The field " +
            translationsField +
            " does not exists in formik.values, check the field name !!"
        );
      }
      const currentDataTranslation = getIn(
        formik.values,
        `${translationsField}`
      );

      const currentTranslationsInDto =
        currentDataTranslation?.dataTranslationValues || [];

      const index = currentTranslationsInDto.findIndex(
        (d) => d.languageId === languageId
      );
      return `${translationsField}.dataTranslationValues.${index}.text`;
    },
    [formik.values, translationsField]
  );

  // RENDER PART
  if (loadingLanguages) return null;

  return (
    <FGCustomInput label={label}>
      {() => (
        <>
          {languages?.map((lang) => (
            <FGTextInput
              key={lang.id}
              name={getFieldName(lang.id)}
              rightElement={<Tag>{lang.code.toUpperCase()}</Tag>}
              {...fieldProps}
            />
          ))}
        </>
      )}
    </FGCustomInput>
  );
};
